/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/Annotations/DraggableAnnotationList" />

import * as React from "react";
import { IObservableArray } from "VSS/Core/Observable";
import { cellFromElement } from "VSSUI/Components/List/List";
import { IListDragDropBehaviorOptions } from "VSSUI/Components/List/ListDragDropBehavior";
import { BoltListDragEvent, IListDropData, IListProps, List, ListDragDropBehavior } from "VSSUI/List";
import { Pointer } from "VSSUI/Util";
import { IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { useBacklogManagementPermission } from "Wit/Common/Controls/Boards/AgileBacklogPermissionContext";
import { findElementAtPoint } from "Wit/Common/Controls/Boards/Draggable";

interface IDraggableAnnotationListProps<T> extends Pick<IListProps<T>, "renderRow">, Pick<IListDragDropBehaviorOptions<T>, "id" | "renderDragImage"> {
    ariaLabel: string;
    dragType: string;
    items: IObservableArray<T>;
    onDropItem: (item: T, parentId: number, previousParentId?: number, newIndex?: number, nextIndex?: number, previousIndex?: number) => void;
    parent: IObservableBoardsWorkItem;
}

// track the highlighted card when dragging
let highlightedCard: HTMLElement | undefined = undefined;

export function DraggableAnnotationList<T>({
    ariaLabel,
    dragType,
    items,
    renderRow,
    onDropItem,
    parent,
    ...dragProps
}: IDraggableAnnotationListProps<T>) {
    const dragEvent = React.useRef<BoltListDragEvent<HTMLElement, T> | undefined>(undefined);
    const hasBacklogManagementPermission = useBacklogManagementPermission();

    const updateBoardOnAnnotationDrag = React.useCallback((event: PointerEvent) => {
        const card = findElementAtPoint(".wit-card", event.pageX, event.pageY);
        const newParentId = Number(card?.dataset.itemid) ?? 0;
        const previousParentId = Number(dragEvent.current?.detail.dataTransfer.secondaryData?.sourceId) ?? 0;
        if (event.type === "pointermove") {
            if (card !== highlightedCard) {
                highlightedCard?.classList.remove("highlighted");

                // don't highlight the card this annotation is currently parented to
                if (newParentId !== previousParentId) {
                    card?.classList.add("highlighted");
                }

                highlightedCard = card;
            }
        } else if (event.type === "pointerup") {
            highlightedCard?.classList.remove("highlighted");
            highlightedCard = undefined;

            // move the item to its new parent if the drop isn't happening within the new parent's list of annotations
            const isTargetingList = !!cellFromElement(event.target as HTMLElement).cellElement;
            if (!isTargetingList && dragEvent.current && newParentId && previousParentId && previousParentId !== newParentId) {
                const item = dragEvent.current.detail.dataTransfer.data;

                // delay a moment so we don't update the lists before the DragDrop behavior gets a chance to clean up
                setTimeout(() => onDropItem(item, newParentId, previousParentId), 0);
            }

            dragEvent.current = undefined;
        }
    }, []);

    const onDragStart = (event: BoltListDragEvent<HTMLElement, T>) => {
        // save off the initial drag event so board update callback has drag event data
        event.persist();
        dragEvent.current = event;

        // the Drag/Drop behavior lets us drag annotations between cards, but only handles dropping
        // into other annotation lists on cards.
        // add a second pointermove event handler so we can process drags over any part of cards.
        // Pointer will handle clean up when pointerup event fires.
        Pointer.setCapture(updateBoardOnAnnotationDrag);
    };

    const onDrop = (event: BoltListDragEvent<HTMLElement, T>, { index }: IListDropData) => {
        const item = event.detail.dataTransfer.data;
        const previousIndex = index === 0 ? undefined : index - 1;
        const nextIndex = index === items.length ? undefined : index;
        const parentId = parent.value.id;
        const previousParentId = Number(event.detail.dataTransfer.secondaryData?.sourceId);
        const parentChanged = parentId !== previousParentId;
        onDropItem(item, parentId, parentChanged ? previousParentId : undefined, index, nextIndex, previousIndex);
    };

    const behaviors = hasBacklogManagementPermission
        ? [
              new ListDragDropBehavior({
                  allowedTypes: [dragType],
                  id: parent.value.id.toString(),
                  onDragStart,
                  onDrop,
                  type: dragType,
                  ...dragProps
              })
          ]
        : undefined;

    return <List behaviors={behaviors} className="body-s margin-top-4 padding-vertical-4" itemProvider={items} virtualize={false} renderRow={renderRow} width="100%" />;
}
